import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { LogOut, Plus, List, Users, Search, BookOpen } from "lucide-react";
import Logo from "../../components/Logo";

export default function AdminShell() {
  const { authChecking, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [subStats, setSubStats] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/admin/subscribers/stats")
        .then((r) => setSubStats(r.data))
        .catch((err) => {
          if (err.response?.status === 401) navigate("/admin/login");
        });
    }
  }, [user, location.pathname, navigate]);

  if (authChecking) {
    return (
      <div
        style={{ background: "#111111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}
        data-testid="admin-loading"
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 32, height: 32, border: "2px solid #CC1A1A", borderTopColor: "transparent", borderRadius: "50%", animation: "rd-spin 0.8s linear infinite", margin: "0 auto 24px" }} />
          <p style={{ color: "#CC1A1A", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            // Checking clearance…
          </p>
          <style>{`@keyframes rd-spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.totp_enabled === false) return <Navigate to="/admin/totp-setup" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const link = (to, icon, label, testId) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`flex items-center gap-2 md:gap-3 px-3 py-3 md:px-6 md:py-4 border text-[0.6rem] md:text-xs font-mono tracking-[0.14em] md:tracking-[0.18em] uppercase transition-colors md:border-0 md:border-l-2 ${
          active
            ? "border-[#CC1A1A] bg-[#1A1A1A] text-white"
            : "border-[#2A2A2A] text-[#9A8F83] hover:text-white hover:bg-[#1A1A1A] md:border-transparent"
        }`}
      >
        {icon} {label}
      </Link>
    );
  };

  return (
    <div className="grid md:grid-cols-[260px_1fr] min-h-screen bg-[#111111] overflow-x-hidden" data-testid="admin-shell">
      <aside className="md:border-r border-[#2A2A2A] bg-[#0B0B0B] py-6 md:py-10">
        <div className="px-6 mb-10 flex items-center gap-3">
          <Logo />
        </div>
        <div className="px-6 mb-6 text-[10px] font-mono tracking-[0.24em] uppercase text-[#666]">
          // TERMINAL
        </div>
        <nav className="grid grid-cols-2 gap-2 px-3 md:flex md:flex-col md:gap-0 md:px-0">
          {link("/admin", <List className="w-4 h-4" />, "ALL FILES", "nav-all-deals")}
          {link("/admin/new", <Plus className="w-4 h-4" />, "NEW FILE", "nav-new-deal")}
          {link("/admin/deal-finder", <Search className="w-4 h-4" />, "DEAL FINDER", "nav-deal-finder")}
          {link("/admin/subscribers", <Users className="w-4 h-4" />, `OPERATORS${subStats ? ` · ${subStats.confirmed}` : ""}`, "nav-subscribers")}
          {link("/admin/guides", <BookOpen className="w-4 h-4" />, "GUIDES", "nav-guides")}
        </nav>

        <div className="hr-dot mx-6 my-8" />

        <div className="px-6 mb-4 text-[10px] font-mono tracking-[0.2em] uppercase text-[#666]">
          <p className="text-white truncate">{user?.email || "admin@restricteddeals.com"}</p>
          <p className="mt-1 text-[#666]">clearance · level 5</p>
        </div>
        <button
          onClick={doLogout}
          className="flex items-center gap-2 px-6 text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83] hover:text-[#CC1A1A] transition-colors"
          data-testid="admin-logout"
        >
          <LogOut className="w-3 h-3" /> REVOKE SESSION
        </button>
      </aside>

      <section className="p-6 md:p-10 bg-[#111111] min-h-screen text-white">
        <Outlet />
      </section>
    </div>
  );
}
